import { useState } from 'react'
import LoginForm from './LoginForm'
import ASINDutyChecker from './ASINDutyChecker'

interface PublicToolsInterfaceProps {
  onLogin: (email: string) => void
  loading: boolean
}

type ExpandedTool = 'login' | 'hts' | null

export default function PublicToolsInterface({ onLogin, loading }: PublicToolsInterfaceProps) {
  const [expandedTool, setExpandedTool] = useState<ExpandedTool>('login')

  const tools = [
    {
      id: 'login' as const,
      title: 'Client Login',
      description: 'Access your shipment management dashboard',
      icon: '🔐',
      component: <LoginForm onLogin={onLogin} loading={loading} />
    },
    {
      id: 'hts' as const,
      title: 'HTS Classifier & Duty Estimator',
      description: 'Get HTS codes and estimate US import duties for Amazon products',
      icon: '📋',
      component: <ASINDutyChecker />
    }
  ]

  const handleToolClick = (toolId: ExpandedTool) => {
    setExpandedTool(expandedTool === toolId ? null : toolId)
  }

  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto'
    }}>
      <div style={{
        display: 'grid',
        gap: '16px'
      }}>
        {tools.map(tool => {
          const isExpanded = expandedTool === tool.id
          
          return (
            <div
              key={tool.id}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: isExpanded ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Tool Header - Always Visible */}
              <div
                onClick={() => handleToolClick(tool.id)}
                style={{
                  padding: '20px',
                  backgroundColor: isExpanded ? '#f9fafb' : '#ffffff',
                  cursor: 'pointer',
                  borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    fontSize: '24px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '8px'
                  }}>
                    {tool.icon}
                  </div>
                  
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h3 style={{
                        margin: 0,
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        {tool.title}
                      </h3>
                      
                      {tool.badge && (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: '600',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          backgroundColor: '#fff3cd',
                          border: '1px solid #ffc107',
                          color: '#856404',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {tool.badge}
                        </span>
                      )}
                    </div>
                    
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      color: '#6b7280',
                      lineHeight: '1.4'
                    }}>
                      {tool.description}
                    </p>
                  </div>
                </div>
                
                {/* Expand/Collapse Arrow */}
                <div style={{
                  fontSize: '20px',
                  color: '#9ca3af',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>
                  ↓
                </div>
              </div>
              
              {/* Tool Content - Expandable */}
              {isExpanded && (
                <div style={{
                  padding: '0',
                  backgroundColor: '#ffffff'
                }}>
                  {tool.component}
                </div>
              )}
            </div>
          )
        })}
      </div>
      
      {/* Footer Info */}
      <div style={{
        marginTop: '40px',
        padding: '20px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <h3 style={{
          margin: '0 0 8px 0',
          fontSize: '16px',
          fontWeight: '600',
          color: '#1f2937'
        }}>
          Need Full Access?
        </h3>
        <p style={{
          margin: '0 0 16px 0',
          fontSize: '14px',
          color: '#6b7280',
          lineHeight: '1.5'
        }}>
          YYZ CBP clients get unlimited access to all tools plus shipment management, 
          commercial invoice generation, and priority support.
        </p>
        <button
          onClick={() => handleToolClick('login')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Login to Your Account
        </button>
      </div>
    </div>
  )
}